import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { 
  ShoppingBag,
  Zap,
  Package,
  Info,
  MessageCircle,
  Phone,
  Mail,
  Facebook,
  Instagram,
  ExternalLink,
  ArrowRight,
  Briefcase
} from 'lucide-react';

const LinksPage = () => {
  const navigate = useNavigate();
  const [language, setLanguage] = useState('en');

  const links = [
    {
      icon: ShoppingBag,
      title: language === 'en' ? 'Get a Quote' : 'Solicitar Cotización',
      desc: language === 'en' ? 'Request pricing for any product' : 'Solicitar precio para cualquier producto',
      color: '#8b1538',
      action: () => navigate('/get-quote')
    },
    {
      icon: Zap,
      title: language === 'en' ? 'Express Shipping (SHEIN)' : 'Envío Exprés (SHEIN)',
      desc: language === 'en' ? '7-10 days delivery' : 'Entrega en 7-10 días',
      color: '#fad24b',
      action: () => navigate('/express-shipping')
    },
    {
      icon: Briefcase,
      title: language === 'en' ? 'Partner With Us' : 'Asóciate con Nosotros',
      desc: language === 'en' ? 'Start your pre-order business' : 'Inicia tu negocio de pre-órdenes',
      color: '#ff8c19',
      action: () => navigate('/partner')
    },
    {
      icon: Package,
      title: language === 'en' ? 'Browse Products' : 'Ver Productos',
      desc: language === 'en' ? 'Trending items from all stores' : 'Artículos populares de todas las tiendas',
      color: '#3dd3ee',
      action: () => navigate('/')
    },
    {
      icon: Info,
      title: language === 'en' ? 'About Us' : 'Acerca de Nosotros',
      desc: language === 'en' ? 'Learn about our story' : 'Conoce nuestra historia',
      color: '#736c64',
      action: () => navigate('/about')
    },
    {
      icon: MessageCircle,
      title: 'WhatsApp',
      desc: language === 'en' ? 'Chat with us directly' : 'Chatea con nosotros',
      color: '#25D366',
      action: () => window.open('https://wa.me/5016735717', '_blank'),
      external: true
    },
    {
      icon: Phone,
      title: language === 'en' ? 'Call Us' : 'Llámanos',
      desc: '(501) 673-5717',
      color: '#8b1538',
      action: () => window.open('tel:+5016735717', '_blank'),
      external: true
    },
    {
      icon: Mail,
      title: 'Email',
      desc: 'athenasonlineshoppingservices@gmail.com',
      color: '#736c64',
      action: () => window.open('mailto:athenasonlineshoppingservices@gmail.com', '_blank'),
      external: true
    }
  ];

  const socialLinks = [
    {
      icon: Facebook,
      name: 'Facebook',
      url: 'https://www.facebook.com/profile.php?id=61584846392424',
      color: '#1877F2'
    },
    {
      icon: Instagram,
      name: 'Instagram',
      url: 'https://www.instagram.com/athenasonlineshoppingservices/',
      color: '#E4405F'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f7f5f2] to-white">
      {/* Header */}
      <div className="pt-12 pb-8 px-4">
        <div className="container mx-auto max-w-2xl text-center">
          <img 
            src="https://customer-assets.emergentagent.com/job_57e2dd96-4e50-451b-8abf-042890e7a51b/artifacts/m648krxt_Athena%27s%20Logo.png"
            alt="Athena's Online Shopping Services"
            className="h-24 w-auto mx-auto mb-6"
          />
          <h1 className="text-3xl font-bold text-[#1e1919] mb-2">
            Athena's Online Shopping Services
          </h1>
          <p className="text-[#736c64]">
            {language === 'en' 
              ? 'Your One-Stop Shop for USA Products in Belize' 
              : 'Tu Tienda Única para Productos de EE.UU. en Belice'}
          </p>
        </div>
      </div>

      {/* Main Links */}
      <div className="px-4 pb-8">
        <div className="container mx-auto max-w-2xl space-y-4">
          {links.map((link, index) => {
            const Icon = link.icon;
            return (
              <Card 
                key={index}
                onClick={link.action}
                className="cursor-pointer hover:shadow-xl transition-all transform hover:scale-105 border-2 hover:border-[#8b1538]/30"
              >
                <CardContent className="p-6 flex items-center gap-4">
                  <div 
                    className="w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${link.color}20` }}
                  >
                    <Icon className="w-7 h-7" style={{ color: link.color }} />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-[#1e1919]">{link.title}</h3>
                    <p className="text-sm text-[#736c64]">{link.desc}</p>
                  </div>
                  {link.external ? (
                    <ExternalLink className="w-5 h-5 text-[#736c64]" />
                  ) : (
                    <ArrowRight className="w-5 h-5 text-[#736c64]" />
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Social Media */}
      <div className="px-4 pb-12">
        <div className="container mx-auto max-w-2xl">
          <h2 className="text-xl font-bold text-center text-[#1e1919] mb-4">
            {language === 'en' ? 'Follow Us' : 'Síguenos'}
          </h2>
          <div className="flex gap-4 justify-center">
            {socialLinks.map((social, index) => {
              const Icon = social.icon;
              return (
                <a
                  key={index}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-16 h-16 rounded-full flex items-center justify-center hover:scale-110 transition-transform"
                  style={{ backgroundColor: social.color }}
                >
                  <Icon className="w-8 h-8 text-white" />
                </a>
              );
            })}
          </div>
        </div>
      </div>

      {/* Language Toggle */}
      <div className="px-4 pb-12">
        <div className="container mx-auto max-w-2xl text-center">
          <button
            onClick={() => setLanguage(language === 'en' ? 'es' : 'en')}
            className="text-[#8b1538] hover:text-[#6b0f2a] font-semibold"
          >
            {language === 'en' ? '🇪🇸 Cambiar a Español' : '🇺🇸 Switch to English'}
          </button>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#1e1919] text-white py-6 px-4">
        <div className="container mx-auto max-w-2xl text-center">
          <p className="text-[#bbb5ae] text-sm mb-3">
            Business Hours: 10 AM - 9 PM (7 Days)
          </p>
          <div className="mb-4 text-sm space-y-1">
            <p className="text-[#bbb5ae]">
              Phone: <a href="tel:+5016735717" data-testid="footer-phone-link" className="text-[#fad24b] hover:text-white transition-colors">6735717</a>
            </p>
            <p className="text-[#bbb5ae]">
              WhatsApp: <a href="https://wa.me/5016735717" target="_blank" rel="noopener noreferrer" data-testid="footer-whatsapp-link" className="text-[#fad24b] hover:text-white transition-colors">Chat with us</a>
            </p>
            <p className="text-[#bbb5ae]">
              Email: <a href="mailto:athenasonlineshoppingservices@gmail.com" data-testid="footer-email-link" className="text-[#fad24b] hover:text-white transition-colors break-all">athenasonlineshoppingservices@gmail.com</a>
            </p>
            <p className="text-[#bbb5ae]">
              <a href="/privacy" data-testid="footer-privacy-link" className="text-[#fad24b] hover:text-white transition-colors">Privacy Policy</a>
            </p>
          </div>
          <p className="text-[#bbb5ae] text-sm">
            © {new Date().getFullYear()} Athena's Online Shopping Services
          </p>
          <p className="text-[#736c64] text-xs mt-2">
            {language === 'en' ? 'Orange Walk, Belize' : 'Orange Walk, Belice'}
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LinksPage;
